import { isAlbum, isCalendar } from "../../_prism-builder/trays/tray-determiner";
import { builderDispatch, WebUI } from "../ui";
import { db, canUseIndexedDB, handleDexieError } from "./db";
import { usePrismItemStore } from 'stores/usePrismItemStore';
import { useUploadResultsStore } from 'stores/useUploadResultsStore';

/**
 * Uploads a file to the API.
 * @param {Object} upload - The upload object.
 * @param {File} compressedBlobAsFile - The compressed blob as a file.
 * @returns {Promise<void>} - A promise that resolves when the file is uploaded.
 */
export async function uploadFileToAPI(upload, compressedBlobAsFile, prismItem = null) {
  const formData = buildFormData(compressedBlobAsFile, { uploadSource: 'uploadIndexedDB', target_album: null });
  formData.append('name', compressedBlobAsFile.name);
  formData.append('file', compressedBlobAsFile);
  const canKeepUsingIndexedDB = await canUseIndexedDB(1000 * 1024 * 1024);
  
  let number_of_files = 0;
  let number_of_errors = 0;
  let imageId;

  try {
    const response = await fetch(
      prismItem
        ? `/edt/img/add/${prismItem.sec}/${prismItem.num}?json=1`
        : upload.uploadUrl,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (response.status !== 200) {
      number_of_files--;
      number_of_errors++;

      db.imageUpload.delete(upload.id).catch((e) => {
        return handleDexieError(e, { from: "uploadFileToAPI" });
      });

      console.error('Error posting files to API', response);
    } else if (upload.resultKey) {
      try {
        const body = await response.json();
        imageId = body?.result?.images?.[0]?.id;
      } catch (e) {
        console.error('Error reading upload response', e);
      }
    }

    number_of_files++;
  } catch (error) {
    number_of_files--;
    number_of_errors++;

    db.imageUpload.delete(upload.id).catch((e) => {
      return handleDexieError(e, { from: "catch uploadFileToAPI" });
    });

    console.error('Error posting files to API', error);
  }
  
  if (upload.resultKey) {
    useUploadResultsStore
      .getState()
      .setResult(
        upload.resultKey,
        number_of_errors === 0 && imageId
          ? { status: 'done', imageId }
          : { status: 'failed' },
      );
  }

  if (number_of_errors > 0) {
    usePrismItemStore.setState(({ failedImagesUploaded}) => ({
      failedImagesUploaded: [
        ...failedImagesUploaded,
        compressedBlobAsFile.name,
      ],
    }));
    settleUpload(upload);
  } else {
    let prismItemIDs = [...new Set(upload.prismItemIDs || [])];

    if (prismItem) {
      if (!prismItemIDs.includes(prismItem.num)) {
        prismItemIDs.push(prismItem.num);
      }
      // A recent upload re-added to this item isn't part of an upload batch,
      // so it reports progress but never settles one into an UPLOAD_DONE.
      builderDispatch.dispatch({
        type: WebUI.UPLOAD_PROGRESS,
      });
    }

    const canPersist =
      window?.featureFlags?.show_recent_uploads &&
      canKeepUsingIndexedDB &&
      !isAlbum() &&
      !isCalendar();      

    if (upload.idbSkipped) {
      settleUpload(upload);
    } else if (canPersist) {
      // useUploadMonitor reports progress when it sees the 'done' record, but
      // settling here too means the batch doesn't depend on it seeing that
      // (unmounted, or the record deleted first). The repeat is a no-op.
      db.imageUpload
        .update(upload.id, {
          status: 'done',
          prismItemIDs,
        })
        .then(
          () => markSettled(upload),
          (e) => {
            settleUpload(upload);
            return handleDexieError(e, { from: 'update DONE' });
          },
        );
    } else {
      settleUpload(upload);
      db.imageUpload.delete(upload.id).catch((e) =>
        handleDexieError(e, {
          from: `canKeepUsingIndexedDB is ${canKeepUsingIndexedDB}`,
        })
      );
    }
  }
}

/**
 * Counts an upload, successful or not, towards the upload batch it was tagged
 * with. Records without a batchId (saved before batching) are ignored.
 * @param {Object} upload - The upload object.
 */
export function settleUpload(upload) {
  builderDispatch.dispatch({ type: WebUI.UPLOAD_PROGRESS });
  markSettled(upload);
}

const markSettled = (upload) =>
  usePrismItemStore.getState().markUploadSettled({
    batchId: upload.batchId,
    key: upload.uploadKey ?? upload.id,
  });

/**
 * Builds a FormData object with the provided file and additional data.
 * @param {File} file - The file to be included in the FormData object.
 * @param {Object} additionalData - Additional data to be included in the FormData object.
 * @returns {FormData} The FormData object containing the file and additional data.
 */
export function buildFormData(file, additionalData) {
  const formData = new FormData();
  const {
    uploadSource: uploadSourceOverride,
    ...otherParams
  } = additionalData;

  const uploadSource = uploadSourceOverride || 'livelink';

  formData.set('source', uploadSource);
  Object.entries(otherParams).forEach(([key, value]) => {
    formData.append(key, value);
  });

  return formData;
}
